import React from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { Tooltip } from '@mui/material';



const CollectionResults = ({ results }) => {
  if (!results || !results.endpointResults || Object.keys(results.endpointResults).length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 font-apple">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium">No collection results available</p>
          <p className="text-sm">Run a collection to see results here</p>
        </div>
      </div>
    );
  }

  const { endpointResults, collectionResults } = results;

  const getStatusIcon = (status) => {
    if (status.includes('PASS')) {
      return <CheckCircle size={14} className="text-green-600" />;
    } else if (status.includes('FAIL')) {
      return <XCircle size={14} className="text-red-600" />;
    } else {
      return <Clock size={14} className="text-yellow-600" />;
    }
  };

  const getStatusColor = (status) => {
    if (status.includes('PASS')) {
      return 'text-green-700 bg-green-50';
    } else if (status.includes('FAIL')) {
      return 'text-red-700 bg-red-50';
    } else {
      return 'text-yellow-700 bg-yellow-50';
    }
  };



  const formatDuration = (seconds) => {
    return `${seconds.toFixed(2)}s`;
  };

  const formatRequestBody = (requestBody) => {
    if (!requestBody) return 'No request body';
    try {
      return JSON.stringify(JSON.parse(requestBody), null, 2);
    } catch (e) {
      return requestBody;
    }
  };

  return (
    <div className="h-full overflow-auto p-6 bg-gray-50 font-apple">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Collection Stats */}
        {collectionResults && (
          <div className="bg-white shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Collection Summary
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Overall collection execution statistics
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {new Date(collectionResults.startTime).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-gray-500">Date</div>
                  <div className="text-sm font-medium text-gray-900 mt-2">
                    {collectionResults.environment}
                  </div>
                  <div className="text-xs text-gray-500">Environment</div>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="text-center p-3 bg-white border border-gray-200">
                  <div className="text-lg font-semibold text-gray-900">{collectionResults.id}</div>
                  <div className="text-xs text-gray-500 mt-1">ID</div>
                </div>
                
                <div className="text-center p-3 bg-white border border-gray-200">
                  <div className="text-lg font-semibold text-gray-900">{collectionResults.allTests}</div>
                  <div className="text-xs text-gray-500 mt-1">Tests</div>
                </div>
                
                <div className="text-center p-3 bg-white border border-gray-200">
                  <div className="text-lg font-semibold text-green-600">{collectionResults.passed}</div>
                  <div className="text-xs text-gray-500 mt-1">Passed</div>
                </div>
                
                <div className="text-center p-3 bg-white border border-gray-200">
                  <div className="text-lg font-semibold text-red-600">{collectionResults.failed}</div>
                  <div className="text-xs text-gray-500 mt-1">Failed</div>
                </div>
                
                <div className="text-center p-3 bg-white border border-gray-200">
                  <div className="text-lg font-semibold text-gray-900">{formatDuration(collectionResults.duration)}</div>
                  <div className="text-xs text-gray-500 mt-1">Duration</div>
                </div>
                
                <div className="text-center p-3 bg-white border border-gray-200">
                  <div className="text-lg font-semibold text-gray-900">{collectionResults.avgResponseTime}ms</div>
                  <div className="text-xs text-gray-500 mt-1">Avg Time</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Results */}
        <div className="bg-white shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Detailed Test Results
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Detailed test results for all endpoints in the collection
            </p>
          </div>

          <div className="p-6">
            {Object.entries(endpointResults).map(([endpointPath, endpointTests]) => {
              return (
                <div key={endpointPath} className="mb-8 last:mb-0">
                  {/* Endpoint Header */}
                  <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 border border-gray-200">
                    <div className="flex items-center space-x-4">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 font-mono">
                          {endpointPath}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {endpointTests.length} test case{endpointTests.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Test Cases */}
                  <div className="space-y-4">
                    {endpointTests.map((testCase, testIndex) => (
                      <div key={testIndex}>
                        {Object.entries(testCase).map(([testCaseName, testCaseData]) => (
                          <div key={testCaseName} className="bg-white border border-gray-200 overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-900">
                                  {testCaseName}
                                </h4>
                                <div className="flex items-center space-x-2">
                                  {Object.values(testCaseData.testResults).map((result, index) => (
                                    <Tooltip key={index} title={result} arrow>
                                      <div className="flex items-center">
                                        {getStatusIcon(result)}
                                      </div>
                                    </Tooltip>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="p-4">
                              {/* Test Results */}
                              <div className="mb-4">
                                <h5 className="text-sm font-medium text-gray-700 mb-3">Test Results</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {Object.entries(testCaseData.testResults).map(([testName, result]) => (
                                    <div
                                      key={testName}
                                      className={`p-3 border border-gray-200 ${getStatusColor(result)}`}
                                    >
                                      <div className="flex items-start space-x-2">
                                        {getStatusIcon(result)}
                                        <div className="flex-1">
                                          <div className="text-sm font-medium">{testName}</div>
                                          <div className="text-xs mt-1 opacity-90">{result}</div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Request Body & Original Response */}
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div>
                                  <h5 className="text-sm font-medium text-gray-700 mb-3">Request Body</h5>
                                  <div className="bg-gray-50 border border-gray-200 p-4">
                                    <pre className="text-sm text-gray-800 overflow-x-auto">
                                      {formatRequestBody(testCaseData.requestBody)}
                                    </pre>
                                  </div>
                                </div>

                                <div>
                                  <h5 className="text-sm font-medium text-gray-700 mb-3">Original Response</h5>
                                  <div className="bg-gray-50 border border-gray-200 p-4">
                                    <pre className="text-sm text-gray-800 overflow-x-auto">
                                      {JSON.stringify(testCaseData.originalResponse, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectionResults;
